import { Component, ComponentType } from '../../../../types';
import { BaseCommandHandler } from '../BaseCommandHandler';
import type { CommandResult } from '../BaseCommandHandler';

interface TimelineEvent {
  id: string;
  title: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  status: 'completed' | 'active' | 'upcoming' | 'blocked';
  icon?: string;
  color?: string;
  children?: TimelineEvent[];
}

class TimelineCommandHandler extends BaseCommandHandler {
  constructor() {
    super();
    this.initializePatterns();
  }

  private initializePatterns(): void {
    this.commandPatterns.set(
      /(?:add|create|new)\s+(?:event|milestone|phase)\s+["']?(.+?)["']?\s*$/i,
      (match: RegExpMatchArray, component: Component) => {
        const events = this.getEvents(component);
        const newEvent: TimelineEvent = {
          id: String(Date.now()),
          title: match[1].trim(),
          status: 'upcoming',
          startDate: new Date().toISOString().split('T')[0],
        };
        return { properties: { ...component.properties, events: [...events, newEvent] } };
      }
    );

    this.commandPatterns.set(
      /(?:remove|delete)\s+(?:event|milestone|phase)\s+["']?(.+?)["']?\s*$/i,
      (match: RegExpMatchArray, component: Component) => {
        const events = this.getEvents(component);
        const title = match[1].trim().toLowerCase();
        const filtered = events.filter(e => e.title.toLowerCase() !== title);
        if (filtered.length === events.length) return null;
        return { properties: { ...component.properties, events: filtered } };
      }
    );

    this.commandPatterns.set(
      /set\s+variant\s+(milestone|gantt)/i,
      (match: RegExpMatchArray, component: Component) => {
        return { properties: { ...component.properties, variant: match[1].toLowerCase() } };
      }
    );

    this.commandPatterns.set(
      /set\s+orientation\s+(horizontal|vertical)/i,
      (match: RegExpMatchArray, component: Component) => {
        return { properties: { ...component.properties, orientation: match[1].toLowerCase() } };
      }
    );

    this.commandPatterns.set(
      /set\s+status\s+(?:of\s+)?["']?(.+?)["']?\s+to\s+(completed|active|upcoming|blocked)/i,
      (match: RegExpMatchArray, component: Component) => {
        const events = this.getEvents(component);
        const title = match[1].trim().toLowerCase();
        const status = match[2].toLowerCase() as TimelineEvent['status'];
        const updated = events.map(e =>
          e.title.toLowerCase() === title ? { ...e, status } : e
        );
        return { properties: { ...component.properties, events: updated } };
      }
    );

    this.commandPatterns.set(
      /mark\s+current\s+["']?(.+?)["']?\s*$/i,
      (match: RegExpMatchArray, component: Component) => {
        const events = this.getEvents(component);
        const title = match[1].trim().toLowerCase();
        const found = events.find(e => e.title.toLowerCase() === title);
        if (!found) return null;
        return { properties: { ...component.properties, currentEventId: found.id } };
      }
    );

    this.commandPatterns.set(
      /add\s+sub-?milestone\s+["']?(.+?)["']?\s+to\s+["']?(.+?)["']?\s*$/i,
      (match: RegExpMatchArray, component: Component) => {
        const events = this.getEvents(component);
        const childTitle = match[1].trim();
        const parentTitle = match[2].trim().toLowerCase();
        const updated = events.map(e => {
          if (e.title.toLowerCase() === parentTitle) {
            const children = e.children || [];
            return {
              ...e,
              children: [...children, {
                id: String(Date.now()),
                title: childTitle,
                status: 'upcoming' as const,
              }],
            };
          }
          return e;
        });
        return { properties: { ...component.properties, events: updated } };
      }
    );

    this.commandPatterns.set(
      /(show|hide)\s+connectors?/i,
      (match: RegExpMatchArray, component: Component) => {
        return { properties: { ...component.properties, showConnectors: match[1].toLowerCase() === 'show' } };
      }
    );

    this.commandPatterns.set(
      /(show|hide)\s+dates?/i,
      (match: RegExpMatchArray, component: Component) => {
        return { properties: { ...component.properties, showDates: match[1].toLowerCase() === 'show' } };
      }
    );

    this.commandPatterns.set(
      /(enable|disable)\s+compact(\s+mode)?/i,
      (match: RegExpMatchArray, component: Component) => {
        return { properties: { ...component.properties, compact: match[1].toLowerCase() === 'enable' } };
      }
    );
  }

  private getEvents(component: Component): TimelineEvent[] {
    const events = component.properties?.events;
    return Array.isArray(events) ? events : [];
  }

  canHandle(_command: string, component: Component): boolean {
    return component.type === ComponentType.HORIZONTAL_TIMELINE;
  }

  async parseCommand(command: string, component: Component): Promise<CommandResult> {
    const result = this.matchPatterns(command, component);
    if (!result) {
      return {
        success: false,
        error: 'Unrecognized timeline command',
        suggestions: await this.getExampleCommands(),
      };
    }
    return {
      success: true,
      command: { type: 'property', changes: result, confidence: 0.9 },
    };
  }

  async getExampleCommands(): Promise<string[]> {
    return [
      'add event "Design Review"',
      'remove event "Phase 1"',
      'set variant gantt',
      'set variant milestone',
      'set orientation vertical',
      'set status of "Phase 1" to completed',
      'mark current "Phase 2"',
      'add sub-milestone "Sprint 1" to "Phase 2"',
      'show connectors',
      'hide dates',
      'enable compact mode',
    ];
  }

  async getSuggestions(component: Component): Promise<string[]> {
    const events = this.getEvents(component);
    const suggestions: string[] = [];

    if (events.length === 0) {
      suggestions.push('add event "Phase 1: Planning"');
    }

    const variant = component.properties?.variant || 'milestone';
    suggestions.push(variant === 'milestone' ? 'set variant gantt' : 'set variant milestone');

    const activeEvent = events.find(e => e.status === 'active');
    if (activeEvent) {
      suggestions.push(`set status of "${activeEvent.title}" to completed`);
    }

    const upcomingEvent = events.find(e => e.status === 'upcoming');
    if (upcomingEvent) {
      suggestions.push(`mark current "${upcomingEvent.title}"`);
    }

    return suggestions;
  }
}

export const timelineCommandHandler = new TimelineCommandHandler();
